/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner, Tooltip } from '@nextui-org/react';
import { IconEdit, IconExclamationCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { IconSchool, IconTeacher } from '../../../assets/icons';
import { classNames, Constants } from '../../../utils';
import ModalCreatePlan from './create-plan';
import { PlansService } from '../../../services';
import { fromPhotos, formatAmount } from '../../../utils'

const MODAL_ACTION = {
  NONE: 0,
  CREATE: 1,
  VIEW: 2,
  EDIT: 3,
  DELETE: 4,
}

const Pricing = () => {
  const [selectedItem, setSelectedItem] = useState();
  const [modalAction, setModalAction] = useState(MODAL_ACTION.NONE);
  const { data, deleteItem, filters, filterBy, isLoading, reload } = useFetchTable();
  const isSchoolSelected = filters.for === Constants.PLAN_FOR.SCHOOL;
  const isTeacherSelected = filters.for === Constants.PLAN_FOR.TEACHER;


  const onSelectItem = (action, item = null) => {
    setSelectedItem(item);
    setModalAction(action);
  }

  const closeModal = (reloading = false) => {
    setSelectedItem(null);
    setModalAction(MODAL_ACTION.NONE);
    if (reloading) reload(true);
  }

  return (
    <div className="min-h-screen px-4 md:px-0">
      {isLoading && (
        <div className="w-screen h-screen fixed inset-0 z-[70] flex justify-center items-center bg-white/30">
          <Spinner />
        </div>
      )}

      <Modal
        size="sm"
        isOpen={modalAction !== MODAL_ACTION.NONE}
        onClose={() => closeModal()}
        backdrop="blur"
        scrollBehavior="outside"
      >
        <ModalContent>
          {(onClose) => {
            if (modalAction === MODAL_ACTION.CREATE) return (
              <ModalCreatePlan
                planFor={filters.for}
                onClose={onClose}
                onSuccess={() => closeModal(true)}
              />
            );
            if (modalAction === MODAL_ACTION.EDIT) return (
              <ModalCreatePlan
                plan={selectedItem}
                planFor={filters.for}
                onClose={onClose}
                onSuccess={() => closeModal(true)}
              />
            );
            if (modalAction === MODAL_ACTION.DELETE) return (
              <ModalDelete
                plan={selectedItem}
                onClose={onClose}
                onDelete={() => {
                  deleteItem(selectedItem);
                  closeModal(true);
                }}
              />
            );
          }}
        </ModalContent>
      </Modal>

      <div className="py-4 px-0">
        <div className="flex flex-row items-start gap-4">
          <Tab
            icon={IconSchool}
            label={`Planes Actuales\nColegios`}
            onClick={() => filterBy(Constants.PLAN_FOR.SCHOOL, 'for')}
            isSelected={isSchoolSelected}
          />
          <Tab
            icon={IconTeacher}
            label={`Planes Actuales\nProfesores`}
            onClick={() => filterBy(Constants.PLAN_FOR.TEACHER, 'for')}
            isSelected={isTeacherSelected}
          />
        </div>

        <Button
          type="button"
          className="bg-green text-white text-md font-semibold pr-2 my-8"
          onClick={() => onSelectItem(MODAL_ACTION.CREATE)}
        >
          <span>Nuevo</span>
          <IconPlus />
        </Button>

        <main className="flex flex-row flex-wrap gap-4">
          {data.map((item, index) => (
            <div key={item.id} className="w-[20rem] rounded-xl flex flex-col p-2 shadow-md bg-white">
              <div className="flex justify-start items-start gap-4">
                <div className="bg-primary p-2 rounded-lg">
                  <img className="w-7 h-7 object-contain" src={fromPhotos(item?.icon)} alt="Plan" />
                </div>
                <div>
                  <p className="font-bold text-sm leading-4 mb-0">{item.name}</p>
                  <p className="font-bold text-xl text-secondary mb-0">${formatAmount(item.price)}</p>
                </div>
              </div>
              <p className="mt-4 font-medium text-xs text-black whitespace-pre-line">{item.description}</p>


              <div className="flex justify-end items-center gap-4 mt-auto">
                <Tooltip content="Editar">
                  <IconEdit className="cursor-pointer" onClick={() => onSelectItem(MODAL_ACTION.EDIT, item)} />
                </Tooltip>
                <Tooltip content="Eliminar" color="danger">
                  <IconTrash className="cursor-pointer text-danger" onClick={() => onSelectItem(MODAL_ACTION.DELETE, item)} />
                </Tooltip>
              </div>
            </div>
          ))}
        </main>
      </div>
    </div>
  );
}

const Tab = ({ icon, isSelected, label, onClick }) => (
  <div
    className="min-w-[13rem] max-w-[18rem] rounded-xl p-2 cursor-pointer shadow-md bg-white"
    onClick={onClick}
  >
    <div className="flex justify-start items-center gap-4">
      <div className={classNames('p-2 rounded-lg', isSelected ? 'bg-primary' : 'bg-zinc-100')}>
        <img
          className={classNames('w-7 h-7 object-contain', isSelected ? 'brightness-0 invert' : '')}
          src={icon} alt="Plan"
        />
      </div>
      <p className="text-md leading-5 font-semibold mb-0 whitespace-pre-line">
        {label}
      </p>
    </div>
  </div>
)

const ModalDelete = ({ plan, onClose, onDelete }) => {
  return (
    <>
      <ModalHeader className="flex flex-col gap-1">Confirmación</ModalHeader>
      <ModalBody>
        <p className="text-center">¿Estás seguro de eliminar este plan?</p>
        <p className="text-center font-medium">{plan?.title}</p>
      </ModalBody>
      <ModalFooter className="justify-evenly">
        <Button variant="light" onPress={onClose}>Cancelar</Button>
        <Button color="danger" onPress={onDelete}>Aceptar</Button>
      </ModalFooter>
    </>
  )
}

const useFetchTable = () => {
  const initialFilters = {
    page: 1,
    perPage: Constants.PER_PAGE,
    search: '',
    since: null,
    until: null,
    for: Constants.PLAN_FOR.SCHOOL,
    level: Constants.USER.LEVELS.SCHOOL,
  };

  const [data, setData] = useState([]);
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);

  const debounceTime = 0;
  const debounce = useRef();

  const fetchData = async () => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      const response = await PlansService.plan.get(filters)
      const { data, ...rest } = response;

      setData(data);
      // setPagination(rest);
      setCanFetch(true);

    } catch (error) {
      setData([]);
      onError(String(error));
    }
  }

  const deleteItem = async (data) => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      await PlansService.plan.destroy(data);
      toast.success('Plan eliminado con éxito');
      fetchData();

    } catch (error) {
      onError(String(error));
    }
    setCanFetch(true);
  }

  const canResetFilter = () => {
    const { page, perPage, ...initial } = initialFilters;
    const { page: _, perPage: __, ...current } = filters;
    const initFilter = JSON.stringify(initial);
    const currFilter = JSON.stringify(current);
    return initFilter !== currFilter;
  }

  const onError = (msg) => toast.error(msg);

  const reload = (inSamePage = false) => {
    setCanFetch(true);
    if (!inSamePage) setFilters(initialFilters);
    else fetchData();
  }

  const filterBy = (value, target) => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setCanFetch(true);
      setFilters({ ...filters, page: 1, [target]: value });
    }, debounceTime);
  }

  useEffect(() => {
    fetchData();
  }, [filters]);

  return {
    canResetFilter: canResetFilter(),
    data,
    deleteItem,
    filters,
    filterBy,
    isLoading: !canFetch,
    reload,
  }
}

export default Pricing;
